import React, { useEffect, useMemo, useRef, useState } from "react";
import "./browser_data_spareparts.css";
import { DeleteOutlined, HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Input, InputRef, message, Modal, Space, Table, TableColumnsType, TableColumnType, Upload, UploadFile, UploadProps } from "antd";
import { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import axios from "axios";
import useSWR from "swr";
import { useNavigate } from "react-router-dom";

interface DataType {
    key: string;
    id: string;
    item_code: string;
    item_name: string;
    brand: string;
    item_class: string;
    model: string;
    warehouse: string;
    qty: number;
}
interface CodeDescriptionType {
    code: string;
    description: string;
}

interface WarehouseGroupType {
    warehouse_code: string;
    warehouse_name: string;
}

interface ModelType {
    code: string;
    description: string;
}

type DataIndex = keyof DataType;
const fetcher = (url: string) => axios.get(url).then(res => res.data);
const BROWSER_DATA_API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/browser-data`;

const BrowserDataSparepats: React.FC = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [sorter, setSorter] = useState<{ field?: string; order?: 'ascend' | 'descend' }>({});
    const [isNavigating, setIsNavigating] = useState(false);
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [tableData, setTableData] = useState<DataType[]>([]);
    const navigate = useNavigate();

    const { data: classesData, error: classesError } = useSWR<CodeDescriptionType[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/item-classes`,
        fetcher,
        { revalidateOnFocus: false, revalidateOnReconnect: false, revalidateIfStale: false }
    );

    const { data: brandsData, error: brandsError } = useSWR<CodeDescriptionType[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/item-brands`,
        fetcher,
        { revalidateOnFocus: false, revalidateOnReconnect: false, revalidateIfStale: false }
    );

    const { data: warehouseGroupsData, error: warehouseGroupsError } = useSWR<WarehouseGroupType[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/warehouse-groups`,
        fetcher,
        { revalidateOnFocus: false, revalidateOnReconnect: false, revalidateIfStale: false }
    );

    const { data: modelsData, error: modelsError } = useSWR<ModelType[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/models`,
        fetcher,
        { revalidateOnFocus: false, revalidateOnReconnect: false, revalidateIfStale: false }
    );

    const swrKey = useMemo(() => {
        const queryParams = new URLSearchParams({
            page: String(page),
            limit: String(pageSize),
        });
        Object.entries(filters).forEach(([key, val]) => {
            if (val) queryParams.append(key, val);
        });
        if (sorter.field && sorter.order) {
            queryParams.append('sortField', sorter.field);
            queryParams.append('sortOrder', sorter.order === 'ascend' ? 'asc' : 'desc');
        }
        return `${BROWSER_DATA_API_URL}?${queryParams.toString()}`;
    }, [page, pageSize, filters, sorter]);

    const { data: swrData, error: swrError, isValidating, mutate: revalidate } = useSWR<{ data: DataType[]; total: number }>(
        swrKey,
        fetcher,
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            keepPreviousData: true
        }
    );

    const brandMap = useMemo(() => {
        const map = new Map<string, string>();
        brandsData?.forEach(item => map.set(item.code, item.description));
        return map;
    }, [brandsData]);

    const classMap = useMemo(() => {
        const map = new Map<string, string>();
        classesData?.forEach(item => map.set(item.code, item.description));
        return map;
    }, [classesData]);

    const warehouseMap = useMemo(() => {
        const map = new Map<string, string>();
        warehouseGroupsData?.forEach(item => map.set(item.warehouse_code, item.warehouse_name));
        return map;
    }, [warehouseGroupsData]);

    const modelMap = useMemo(() => {
        const map = new Map<string, string>();
        modelsData?.forEach(item => map.set(item.code, item.description));
        return map;
    }, [modelsData]);

    useEffect(() => {
        if (brandsError) {
            console.error("Error fetching brands data:", brandsError);
            message.error("Gagal memuat data brand.");
        }
        if (classesError) {
            console.error("Error fetching classes data:", classesError);
            message.error("Gagal memuat data item class.");
        }
        if (warehouseGroupsError) {
            console.error("Error fetching warehouse groups data:", warehouseGroupsError);
            message.error("Gagal memuat data grup gudang.");
        }

        if (modelsError) {
            console.error("Error fetching models data:", modelsError);
            message.error("Gagal memuat data model.");
        }
    }, [brandsError, classesError, warehouseGroupsError, modelsError]);

    useEffect(() => {
        if (swrData && swrData.data) {
            const mappedData = swrData.data.map(item => ({
                ...item,
                key: item.id.toString(),
                qty: item.qty ?? 0
            }));
            setTableData(mappedData);
        }
        if (swrError) {
            console.error("Error fetching browser data:", swrError);
            message.error("Gagal memuat data spareparts. Coba lagi nanti.");
        }
    }, [swrData, swrError]);
    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex as string);
        setFilters(prev => ({ ...prev, [dataIndex]: selectedKeys[0] }));
        setPage(1);
    };

    const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn('');
        setFilters(prev => {
            const next = { ...prev };
            delete next[dataIndex];
            return next;
        });
        setPage(1);
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filteredValue: filters[dataIndex as string] ? [filters[dataIndex as string]] : null,
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Cari ${dataIndex.replace('_', ' ')}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Cari
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        Tutup
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        render: (text, record) => {
            let displayValue: string | number | null = text;
            if (dataIndex === 'brand') {
                displayValue = brandMap.get(text as string) ?? text;
            } else if (dataIndex === 'item_class') {
                displayValue = classMap.get(text as string) ?? text;
            } else if (dataIndex === 'warehouse') {
                displayValue = text;
            } else if (dataIndex === 'model') {
                displayValue = modelMap.get(text as string) ?? text;
            }
            const textToHighlight = displayValue ? displayValue.toString() : '';
            return searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={textToHighlight}
                />
            ) : (
                displayValue
            );
        }
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Item Code',
            dataIndex: 'item_code',
            key: 'item_code',
            width: '15%',
            ...getColumnSearchProps('item_code'),
            sorter: true,
            sortOrder: sorter.field === 'item_code' ? sorter.order : undefined,
            onCell: () => {
                return {
                    style: { whiteSpace: 'nowrap' }
                };
            },
        },
        {
            title: 'Item Name',
            dataIndex: 'item_name',
            key: 'item_name',
            width: '20%',
            ...getColumnSearchProps('item_name'),
            sorter: true,
            sortOrder: sorter.field === 'item_name' ? sorter.order : undefined,
            onCell: () => {
                return {
                    style: { whiteSpace: 'nowrap' }
                };
            },
        },
        {
            title: 'Brand',
            dataIndex: 'brand',
            key: 'brand',
            width: '10%',
            ...getColumnSearchProps('brand'),
            sorter: true,
            sortOrder: sorter.field === 'brand' ? sorter.order : undefined,
            render: (text: string) => brandMap.get(text) ?? text,
            onCell: () => {
                return {
                    style: { whiteSpace: 'nowrap' }
                };
            },
        },
        {
            title: 'Item Class',
            dataIndex: 'item_class',
            key: 'item_class',
            width: '10%',
            ...getColumnSearchProps('item_class'),
            sorter: true,
            sortOrder: sorter.field === 'item_class' ? sorter.order : undefined,
            render: (text: string) => classMap.get(text) ?? text,
            onCell: () => {
                return {
                    style: { whiteSpace: 'nowrap' }
                };
            },
        },
        {
            title: 'Model',
            dataIndex: 'model',
            key: 'model',
            width: '10%',
            ...getColumnSearchProps('model'),
            sorter: true,
            sortOrder: sorter.field === 'model' ? sorter.order : undefined,
            onCell: () => {
                return {
                    style: { whiteSpace: 'nowrap' }
                };
            },
        },
        {
            title: 'Warehouse',
            dataIndex: 'warehouse',
            key: 'warehouse',
            width: '10%',
            ...getColumnSearchProps('warehouse'),
            sorter: true,
            sortOrder: sorter.field === 'warehouse' ? sorter.order : undefined,
            render: (text: string) => text,
            onCell: () => {
                return {
                    style: { whiteSpace: 'nowrap' }
                };
            },
        },
        {
            title: 'Qty',
            dataIndex: 'qty',
            key: 'qty',
            width: '10%',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            sortOrder: sorter.field === 'qty' ? sorter.order : undefined,
            onCell: () => {
                return {
                    style: { whiteSpace: 'nowrap' }
                };
            },
            render: (qty: number) => qty ?? 0,
        }
    ];

    const handleRowDoubleClick = (record: DataType) => {
        if (isNavigating) return; 
        setIsNavigating(true);
        console.log("Row double clicked:", record);
        if (record.id) {
            navigate(`/dashboard/spareparts_master/${record.id}`);
        } else {
            message.error("ID item tidak ditemukan untuk navigasi.");
            setIsNavigating(false);
        }
    };

    return (

        <div id="iklk" style={transformStyle}>
            <div className="gjs-grid-row" id="iadvh">
                <div className="gjs-grid-column" id="imzdl">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                href: "#/dashboard/spareparts_master",
                                title: (
                                    <>
                                        <span>Data SpareParts</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/browser_data_spareparts",
                                title: (
                                    <>
                                        <span>Browser Data Spareparts</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/status_import_spareparts_master",
                                title: "Status Update Data Sparepart Master",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="fdsasd">
                <div className="gjs-grid-column" id="ccmxks">
                    <Table<DataType>
                        columns={columns}
                        dataSource={tableData}
                        bordered
                        size="small"
                        loading={isNavigating || (!swrData && !swrError) || !classesData || !brandsData || !warehouseGroupsData || !modelsData}
                        rowKey="key"
                        pagination={{
                            current: page,
                            pageSize: pageSize,
                            total: swrData?.total || 0,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                        }}
                        onChange={(pagination, tblFilters, sorter: any) => {
                            setPage(pagination.current || 1);
                            setPageSize(pagination.pageSize || 10);
                            const nextFilters: Record<string, string> = {};
                            Object.entries(tblFilters).forEach(([key, val]) => {
                                if (val && val[0]) {
                                    nextFilters[key] = String(val[0]);
                                }
                            });
                            setFilters(nextFilters);
                            if (sorter && sorter.field) {
                                setSorter({
                                    field: sorter.field as string,
                                    order: sorter.order,
                                });
                            } else {
                                setSorter({});
                            }
                        }}
                        onRow={(record) => ({
                            onDoubleClick: () => handleRowDoubleClick(record),
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default BrowserDataSparepats;
